//Imports
import React, { useCallback, useEffect, useState } from "react";

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Controls
import {
    Button,
    Form,
    Input,
    Modal,
    TextArea,
    Select,
} from "@gpstrackit/gtc-react-controls";
import { Grid } from "@mui/material";

//Components
import IconPicker from 'Modules/.common/components/IconPicker';
import {
    NotificationManager,
    NotificationContainer,
} from "Components/Notifications";

//Slice
import {
    manageWorkOrderSearchWorkOrderStatus,
    manageWorkOrderCreateWorkOrderStatus,
    manageWorkOrderEditWorkOrderStatus,
    manageWorkOrderGetWorkOrderStatus,
    reset,
} from "./redux/slice";

const INIT_DATA = {
    name: undefined,
    description: undefined,
    category: undefined,
    color: '8677D9'
};

const defaultColor = '8677D9';

import './index.scss'

const CreateEditManageWorkOrderStatus = ({ refresh }) => {

    //Local States
    const [formData, setFormData] = useState(INIT_DATA);
    const { messages } = useIntl();

    //Form refs
    const createEditWorkOrderTypeFormRef = Form.useForm();

    //Redux States
    const { loading, open, create, update, workOrderStatus, workOrderStatusId, error } = useSelector((state) => state.manageCreateEditWorkOrderStatusRedux);

    const dispatch = useDispatch();

    //Handlers and utils
    const handleClose = () => {
        setFormData(INIT_DATA);
        dispatch(reset());
    };

    const handleSuccess = () => {
        NotificationManager.success(
            messages['manage_createEditWorkOrderStatus_createSuccess'],
            messages["success"],
            3000
        );
        refresh();
        handleClose();
    };

    let workOrderStatusList = [
        {
            id: "closed",
            name: messages['manage_createEditWorkOrderStatus_closed']
        },
        {
            id: "inprogress",
            name: messages['manage_createEditWorkOrderStatus_inprogress']
        },
        {
            id: "todo",
            name: messages['manage_createEditWorkOrderStatus_todo']
        },
        {
            id: "inroute",
            name: messages['manage_createEditWorkOrderStatus_inroute']
        }
    ]

    const applyChanges = useCallback(() => {
        const { ...otherFormData } = formData || {};
        const payload = {
            ...otherFormData,
            ...(workOrderStatusId && { id: workOrderStatusId }),
            category: formData?.category?.id
        };

        if (workOrderStatusId) {
            dispatch(manageWorkOrderEditWorkOrderStatus(payload));
        } else {
            dispatch(manageWorkOrderCreateWorkOrderStatus(payload));
        }
    }, [formData, workOrderStatusId, dispatch]);

    useEffect(() => {
        if (create || update) {
            handleSuccess();
        }
    }, [create, update]);

    useEffect(() => {
        if (error) {
            let currentError = error?.error
            if (currentError.includes("duplicated")) {
                currentError = messages['manage_createEditWorkOrderStatus_duplicatedName']
            }
            NotificationManager.error(
                currentError || messages["errorMessage"],
                messages["error"],
                3000
            );
            dispatch(reset({ error: null }));
        }
    }, [error])

    useEffect(() => {
        if (workOrderStatusId) {
            dispatch(manageWorkOrderGetWorkOrderStatus({ id: workOrderStatusId }))
        }
    }, [workOrderStatusId])

    useEffect(() => {
        if (workOrderStatus && open) {

            let category = workOrderStatusList.find((item) => item?.id === workOrderStatus.category)

            let payload = {
                ...workOrderStatus,
                category: category
            }
            setFormData(payload);
            createEditWorkOrderTypeFormRef.setValues(payload);
        }
        if (open) {
            dispatch(manageWorkOrderSearchWorkOrderStatus({}))
        }
    }, [workOrderStatus, open])

    return (
        <Modal
            title={workOrderStatusId ? `${messages['manage_createEditWorkOrderStatus_editTitle']} ${workOrderStatus?.name ? `"${workOrderStatus?.name}"` : ""}` : messages['manage_createEditWorkOrderStatus_createTitle']}
            open={open}
            handleClose={handleClose}
            position="right"
            style={{ zIndex: "auto" }}
            size="small"
            loading={loading}
            footer={
                <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Button
                        isDisabled={loading}
                        style={{ marginRight: 8 }}
                        onClick={handleClose}
                        variant="secondary"
                    >
                        {messages["cancel"]}
                    </Button>
                    <Button
                        isDisabled={loading}
                        onClick={() => {
                            createEditWorkOrderTypeFormRef?.submit();
                        }}
                        variant="primary"
                    >
                        {messages["save"]}
                    </Button>
                </div>
            }
        >
            <div style={{ padding: 24 }}>
                <Form
                    ref={createEditWorkOrderTypeFormRef.ref}
                    loading={false}
                    onValidSubmit={() => applyChanges()}
                    onChangeValues={() => { }}
                >
                    <Grid
                        container
                        wrap="nowrap"
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        sx={{ gridGap: 10 }}
                    >
                        <Grid item className="workorderstatus-icon-container">
                            <IconPicker
                                color={formData?.color || defaultColor}
                                icon={null}
                                options={["color"]}
                                onChange={(color, icon) => {
                                    setFormData({ ...formData, color: color })
                                }}
                            />
                        </Grid>
                        <Grid item xs className="workorderstatus-input-container">
                            <Form.Item
                                name="name"
                                label={messages['manage_createEditWorkOrderStatus_name']}
                                rules={[
                                    { required: true, message: messages["required_field"] },
                                ]}
                            >
                                <Input
                                    id="name"
                                    type="text"
                                    placeholder={messages['manage_createEditWorkOrderStatus_name']}
                                    onChange={(value) => setFormData({ ...formData, name: value })}
                                    maxLength={20}
                                />
                            </Form.Item>
                        </Grid>
                    </Grid>
                    <Form.Item
                        name="category"
                        label={messages['manage_createEditWorkOrderStatus_category']}
                        rules={[
                            { required: true, message: messages["required_field"] },
                        ]}
                    >
                        <Select
                            id="category"
                            options={workOrderStatusList}
                            textField="name"
                            valueField="id"
                            placeholder={messages['manage_createEditWorkOrderStatus_category']}
                            style={{ width: "100%" }}
                            onChange={(value) => setFormData({ ...formData, category: value })}
                            isSearchable
                            isClearable
                        />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label={messages['manage_createEditWorkOrderStatus_description']}
                    >
                        <TextArea
                            id="description"
                            placeholder={messages['manage_createEditWorkOrderStatus_description']}
                            maxLength={1000}
                            onChange={(value) => setFormData({ ...formData, description: value })}
                        />
                    </Form.Item>
                </Form>
            </div>
            <NotificationContainer />
        </Modal>
    );
};

export default CreateEditManageWorkOrderStatus;